/**
 * Compact TTS filter — speed / pitch / energy / evenness.
 * Premium (Member) only. Locked strip still shows so Standard users see the unlock.
 */
import { memo } from "react";

import { SUITE_MEMBER_PRICE_USD } from "../suite/access";
import {
  DEFAULT_VOICE_FILTER,
  type TtsVoiceFilter,
} from "../lib/voiceFilter";

type Props = {
  filter: TtsVoiceFilter;
  onChange: (partial: Partial<TtsVoiceFilter>) => void;
  onReset: () => void;
  isMember: boolean;
  onUnlock: () => void;
};

export const TtsFilterStrip = memo(
  ({ filter, onChange, onReset, isMember, onUnlock }: Props) => {
    return (
      <div
        className={`vf-filter${isMember ? "" : " vf-filter--locked"}`}
        aria-label="TTS filter"
      >
        <span className="vf-house-label">Filter</span>
        <label className="vf-filter-dial" title="Speed">
          Spd
          <input
            type="range"
            min={0.7}
            max={1.3}
            step={0.02}
            value={filter.speed}
            disabled={!isMember}
            onChange={(e) => onChange({ speed: Number(e.target.value) })}
          />
          <em>{filter.speed.toFixed(2)}</em>
        </label>
        <label className="vf-filter-dial" title="Pitch (semitones)">
          Pit
          <input
            type="range"
            min={-4}
            max={4}
            step={0.1}
            value={filter.pitch}
            disabled={!isMember}
            onChange={(e) => onChange({ pitch: Number(e.target.value) })}
          />
          <em>{filter.pitch.toFixed(1)}</em>
        </label>
        <label className="vf-filter-dial" title="Energy">
          En
          <input
            type="range"
            min={0}
            max={1}
            step={0.02}
            value={filter.energy}
            disabled={!isMember}
            onChange={(e) => onChange({ energy: Number(e.target.value) })}
          />
          <em>{filter.energy.toFixed(2)}</em>
        </label>
        <label className="vf-filter-dial" title="Evenness">
          Ev
          <input
            type="range"
            min={0}
            max={1}
            step={0.02}
            value={filter.evenness}
            disabled={!isMember}
            onChange={(e) => onChange({ evenness: Number(e.target.value) })}
          />
          <em>{filter.evenness.toFixed(2)}</em>
        </label>
        {isMember ? (
          <button type="button" className="vf-chip" onClick={onReset}>
            Reset
          </button>
        ) : (
          <button
            type="button"
            className="vf-chip"
            title={`TTS filter needs Suite Member ($${SUITE_MEMBER_PRICE_USD}/mo)`}
            onClick={onUnlock}
          >
            Member · filter
          </button>
        )}
      </div>
    );
  },
);

TtsFilterStrip.displayName = "TtsFilterStrip";

export { DEFAULT_VOICE_FILTER };
